import { observer } from 'mobx-react';
import React, { ReactNode } from 'react';
import { ModalHeader, ModalBody, Input, Spinner } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import './SearchModal.scss';
import { getAppContext } from '~/lib/AppContext';
import { serverRoute, networkRoute, loadBalancerRoute, domainRoute } from '~/lib/helper/RouteHelper';

import { SearchStore } from '~/lib/store/SearchStore';
import { primaryInterface } from '~/lib/helper/NetworkHelper';
import { logoForDistribution } from '~/lib/helper/OperatingSystemHelper';
import { LoadStatus } from '~/lib/store/LoadingTracker';

type SearchModalProps = {
    searchStore: SearchStore;
    onClose: () => void;
};

type SearchResultGroupProps = {
    title: string;
    children: ReactNode;
    loading: boolean;
};

function SearchResultGroup({ title, children, loading }: SearchResultGroupProps) {
    const itemCount = React.Children.count(children);
    if (itemCount === 0 && !loading) {
        return null;
    }

    return (
        <div className="searchResultGroup">
            <h5>
                {title}
                {loading && itemCount === 0 && <Spinner size="sm" className="ms-1" />}
            </h5>
            <ul className="searchResultList">{children}</ul>
        </div>
    );
}

type SearchResultProps = {
    href: string;
    className?: string;
    onSelect: () => void;
    children: ReactNode;
};

function SearchResult({ href, className, onSelect, children }: SearchResultProps) {
    return (
        <li className={`searchResult ${className || ''}`}>
            <a href={href} onClick={onSelect}>
                {children}
            </a>
        </li>
    );
}

@observer
export default class SearchModal extends React.Component<SearchModalProps> {
    inputRef = React.createRef<HTMLInputElement>();

    get searchStore() {
        return this.props.searchStore;
    }

    isLoading() {
        return this.searchStore.loader.status === LoadStatus.Loading;
    }

    componentDidMount() {
        // refresh resources and search query
        void this.searchStore.loadResources();
        this.searchStore.setSearchQuery('');

        // Focus deferred a tick: reactstrap mounts this input into its portal after this handler runs,
        // so it doesn't exist yet — a reactstrap-owned commit, not ours. See PanelSite.md "React 18 commit timing".
        setTimeout(() => {
            this.inputRef.current?.focus();
        }, 0);
    }

    componentWillUnmount() {
        // abort any in-flight resource loads when the modal closes
        this.searchStore.cancel();
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            const currentElement = event.target as HTMLElement;
            // we only want to handle keys for search results or for the input field
            const isSearchResult = currentElement.parentElement?.classList.contains('searchResult');
            if (!isSearchResult && currentElement !== this.inputRef.current) {
                return;
            }

            event.preventDefault();
            const modalBody = event.currentTarget;
            const allSearchResults = Array.from(modalBody.querySelectorAll(`.searchResult a`));
            // if we are not a search result then the first result is the current Index
            const currentIndex = isSearchResult ? allSearchResults.indexOf(currentElement) : 0;

            let nextElement: Element | undefined = undefined;

            if (event.key === 'ArrowDown') {
                nextElement = allSearchResults[currentIndex + 1];
            } else {
                if (currentIndex > 0) {
                    nextElement = allSearchResults[currentIndex - 1];
                } else {
                    nextElement = this.inputRef.current ?? undefined;
                }
            }

            if (nextElement instanceof HTMLElement) {
                nextElement.focus();
            }
        }
    }

    serverResults() {
        return (
            <SearchResultGroup title="Servers" loading={this.isLoading()}>
                {this.searchStore.filteredResources.servers.map((server) => (
                    <SearchResult key={server.id} href={serverRoute(server)} onSelect={this.props.onClose}>
                        <img
                            src={logoForDistribution(server.image.distribution)}
                            alt={server.image.distribution ?? ''}
                        />
                        <span className="name">{server.name}</span>
                        <span className="ip">{primaryInterface(server)?.ip_address}</span>
                    </SearchResult>
                ))}
            </SearchResultGroup>
        );
    }

    loadBalancerResults() {
        return (
            <SearchResultGroup title="Load Balancers" loading={this.isLoading()}>
                {this.searchStore.filteredResources.loadBalancers.map((loadBalancer) => (
                    <SearchResult
                        key={loadBalancer.id}
                        href={loadBalancerRoute(loadBalancer)}
                        onSelect={this.props.onClose}
                    >
                        <i className="icon ion-md-shuffle" />
                        <span className="name">{loadBalancer.name}</span>
                        <span className="ip">{loadBalancer.ip}</span>
                    </SearchResult>
                ))}
            </SearchResultGroup>
        );
    }

    networkResults() {
        return (
            <SearchResultGroup title="Networks" loading={this.isLoading()}>
                {this.searchStore.filteredResources.networks.map((network) => (
                    <SearchResult key={network.id} href={networkRoute(network)} onSelect={this.props.onClose}>
                        <i className="icon ion-md-git-network" />
                        <span className="name">{network.name}</span>
                        <span className="ip">{network.ip_range}</span>
                    </SearchResult>
                ))}
            </SearchResultGroup>
        );
    }

    domainResults() {
        return (
            <SearchResultGroup title="Domains" loading={this.isLoading()}>
                {this.searchStore.filteredResources.domains.map((domain) => (
                    <SearchResult
                        key={domain.domain_name}
                        href={`${domainRoute(domain.domain_name)}/registration`}
                        onSelect={this.props.onClose}
                    >
                        <i className="icon ion-md-globe" />
                        <span className="name">{domain.domain_name}</span>
                    </SearchResult>
                ))}
            </SearchResultGroup>
        );
    }

    onSubmit() {
        const url = this.searchStore.getFirstResultUrl();
        if (url) {
            this.props.onClose();
            getAppContext().navigate(url);
        }
    }

    render(): ReactNode {
        return (
            <DialogModal
                className="searchModal"
                size="lg"
                isOpen={true}
                toggle={() => this.props.onClose()}
                fade={false}
                labelledBy="search-modal-title"
            >
                <ModalHeader toggle={() => this.props.onClose()} tag="div">
                    {/* id on the title span, not the ModalHeader — see PanelSite.md "Modals". */}
                    <span id="search-modal-title">Search for services and domains by name or ip.</span>
                </ModalHeader>
                <ModalBody onKeyDown={(e) => this.handleKeyDown(e)}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            this.onSubmit();
                        }}
                    >
                        <Input
                            type="text"
                            aria-label="Search"
                            innerRef={this.inputRef}
                            placeholder="host.example.com"
                            value={this.searchStore.searchQuery}
                            onChange={(e) => {
                                this.searchStore.setSearchQuery(e.target.value);
                            }}
                        />
                    </form>
                    {this.serverResults()}
                    {this.loadBalancerResults()}
                    {this.networkResults()}
                    {this.domainResults()}
                </ModalBody>
            </DialogModal>
        );
    }
}
